import React, { useCallback, useState } from 'react'
import DetailModalWrapper from '../../../components/DetailModalWrapper'
import SimpleTable from '../../../components/UI/SimpleTable'
import Accordion from '../../../components/UI/Accordion'
import { useGetNaturalPersonByIdQuery, useEditNaturalPersonMutation, useChangeNaturalPersonStatusMutation } from '../../../store/naturalPersons/naturalPersons.api'
import { useGetAddressesQuery, useGetClientOrdersQuery } from '../../../store/api'
import CreateModal from '../../../components/CreateModal'
import { naturalPersonsInputs, naturalPersonsInputsData } from '../../../constants/naturalPersonsPage'
import { schema } from './schema'
import Table from '../../../components/Table'
import { addressesColumns, ordersColumns } from '../../../constants/clientsPage'
import dateFormat from 'dateformat'
import TableDateFilter from '../../../components/UI/TableDateFilter'
import Button from '../../../components/UI/Button'
import { exportHandler, formatPhone } from '../../../helpers'
import s from './index.module.scss'
import classNames from 'classnames'
import bankId from '../../../assets/images/bankId.svg'
import { useAppSelector } from '../../../hooks/useAppSelector'
import Region from '../../../components/UI/Region'
import { useActions } from '../../../hooks/useActions'

interface IProps {
	id: string | number
	closeHandler: () => void
}

const NaturalPersonsDetailModal: React.FC<IProps> = React.memo(({ id, closeHandler }) => {
	const { permissions } = useAppSelector((state) => state.common)

	const { setOrderId } = useActions()
	
	const [sort, setSort] = useState({})
	const [perPage, setPerPage] = useState<number>(30)
	const [editModalIsVisible, setEditModalIsVisible] = useState<boolean>(false)
	const [ordersDateFrom, setOrdersDateFrom] = useState('')
	const [ordersDateTo, setOrdersDateTo] = useState('')

	const ordersDateQuery = {
		...(ordersDateFrom && { dateFrom: ordersDateFrom }),
		...(ordersDateTo && { dateTo: ordersDateTo }),
	}

	const exportOrdersQuery = Object.entries(ordersDateQuery).reduce((prev, [key, value], index) => `${prev}${index > 0 ? '&' : ''}${key}=${value}`, '')

	const { data, isLoading } = useGetNaturalPersonByIdQuery({ id })
	const { data: addresses, isLoading: addressesLoading } = useGetAddressesQuery({ id: data?.userId }, { skip: !data?.userId })
	const { data: orders, isLoading: ordersLoading, isFetching } = useGetClientOrdersQuery({ userId: data?.userId, perPage, ...sort, ...ordersDateQuery }, { skip: !data?.userId })
	const [editFunc, { error, isLoading: isEditing }] = useEditNaturalPersonMutation()
	const [changeStatus] = useChangeNaturalPersonStatusMutation()

	const detailInfo = [
		{
			title: 'Номер телефону',
			value: data?.phone ? (
				<a className={s.link} href={`tel:${data?.phone.replaceAll('+', '')}`}>
					{formatPhone(data?.phone) || '-'}
				</a>
			) : (
				''
			),
		},
		{ title: 'E-mail', value: data?.email || '' },
		{
			title: 'Дата реєстрації',
			value: data?.registeredAt ? dateFormat(data.registeredAt, 'dd.mm.yyyy') : '',
		},
		{ title: 'РНКОПП', value: data?.inn || '' },
		{ title: 'Паспортні дані', value: data?.passport || '' },
		{ title: 'Адреса', value: data?.registration || '' },
		{
			title: 'Дата народження',
			value: data?.birthdate ? dateFormat(data.birthdate, 'dd.mm.yyyy') : '',
		},
	]

	const changeSort = useCallback((name: string, value: string) => {
		setSort({ [`sort[${name}]`]: value })
	}, [])

	const fetch = useCallback(() => {
		if (orders?.total && perPage < orders.total && !isFetching) {
			setPerPage((prev) => prev + 30)
		}
	}, [orders?.total, isFetching])

	const editHandler = (newData: naturalPersonsInputs) => {
		newData.birthdate = newData?.birthdate ? dateFormat(newData.birthdate, 'yyyy-mm-dd') : ''
		newData.discountPerLiter = Number(newData?.discountPerLiter) * 100

		const { inn, ...rest } = newData
		
		editFunc({
			id,
			data: {
				...rest,
				...(inn !== data?.inn && { inn }),
				regionId: data?.region?.id || 0
			},
		})
			.unwrap()
			.then(() => setEditModalIsVisible(false))
	}

	const blockHandler = () => {
		changeStatus({ id })
	}

	return (
		<DetailModalWrapper
			isLoading={isLoading || addressesLoading || ordersLoading}
			closeHandler={closeHandler}
			headerTitle={
				<span
					className={classNames(s.title, {
						[s.titlePadding]: data?.registeredInBankId,
					})}
				>
					{data?.fio}
				</span>
			}
			headerSubtitle={
				<span
					className={classNames({
						[s.titlePadding]: data?.registeredInBankId,
					})}
				>
					{data?.registeredInBankId && (
						<span className={s.icon}>
							<img src={bankId} alt="Icon" />
						</span>
					)}
					ID {data?.userId}
				</span>
			}
			isReversedTitles
			status={data?.status}
			deleted={data?.deleted}
			editHandler={() => setEditModalIsVisible(true)}
			blockHandler={blockHandler}
			disableEdit={!permissions.includes('CLIENTS_UPDATE')}
		>
			<div 
				className={classNames(s.discount, {
					[s.titlePadding]: data?.registeredInBankId,
				})}
			>
				<div>
					Знижка <span>{(data?.discountPerLiter || 0).toFixed(2)}</span> грн/л
				</div>
			</div>

			<Region
				region={data?.region?.name}
				className={data?.registeredInBankId ? s.titlePadding : ''}
			/>

			<DetailModalWrapper.Title>Персональна інформація</DetailModalWrapper.Title>
			<SimpleTable data={detailInfo} />

			<Accordion title={'Історія замовлень'}>
				<div className={s.wrapper}>
					<TableDateFilter setDateFrom={setOrdersDateFrom} setDateTo={setOrdersDateTo} />
					<Button variant={'export'} onClick={() => exportHandler(`users/${data?.userId}/orders/export?${exportOrdersQuery}`, 'Замовлення')} />
				</div>
				<Table data={orders?.data || []} columns={ordersColumns} maxHeight={'500px'} sortHandler={changeSort} onDynamicFetch={fetch} showDetail={setOrderId} />
			</Accordion>
			<Accordion title={'Адреси'}>
				<Table data={addresses?.data || []} columns={addressesColumns} maxHeight={'500px'} />
			</Accordion>

			{editModalIsVisible && (
				<CreateModal<naturalPersonsInputs>
					title={'Редагувати фізичну особу'}
					inputs={naturalPersonsInputsData}
					schema={schema}
					initialData={data}
					isLoading={isEditing}
					error={error && 'data' in error ? error.data.message : ''}
					onSubmit={editHandler}
					closeHandler={() => setEditModalIsVisible(false)}
				/>
			)}
		</DetailModalWrapper>
	)
})

export default NaturalPersonsDetailModal
